import {useEffect, useMemo, useState} from 'react'
import {
  ArrowsLeftRight,
  ChartLineUp,
  List,
  Moon,
  PlugsConnected,
  SignOut,
  SquaresFour,
  Sun,
  Target,
  Wallet,
  X,
} from '../components/PhosphorIcons'
import {NavLink, Outlet, useLocation} from 'react-router-dom'
import {useAuth} from '../modules/auth'
import {useTheme} from '../theme/ThemeProvider'

const navigationGroups = [
  {
    label: 'Рабочее пространство',
    items: [
      {to: '/dashboard', label: 'Обзор', icon: SquaresFour, end: true},
      {to: '/portfolio', label: 'Портфель', icon: Wallet},
      {to: '/instruments', label: 'Инструменты', icon: ChartLineUp},
    ],
  },
  {
    label: 'Управление',
    items: [
      {to: '/strategies', label: 'Стратегии', icon: Target},
      {to: '/trading/standing-orders', label: 'Постоянные заявки', icon: ArrowsLeftRight},
    ],
  },
  {
    label: 'Система',
    items: [{to: '/settings/connectors', label: 'Коннекторы', icon: PlugsConnected}],
  },
] as const

const AppShell = () => {
  const [isNavigationOpen, setIsNavigationOpen] = useState(false)
  const location = useLocation()
  const {currentUser, logout} = useAuth()
  const {resolvedTheme, toggleTheme} = useTheme()

  useEffect(() => {
    setIsNavigationOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsNavigationOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  const displayName = useMemo(() => {
    const fullName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ')
    return fullName || currentUser?.username || currentUser?.email || 'Пользователь'
  }, [currentUser])

  const initials = useMemo(() => {
    const parts = displayName.trim().split(/\s+/).filter(Boolean)
    return parts
      .slice(0, 2)
      .map((part) => part[0]?.toLocaleUpperCase('ru-RU'))
      .join('')
  }, [displayName])

  const handleLogout = () => {
    logout()
  }

  return (
    <div className='ui-app-shell'>
      <button
        className={`ui-sidebar-backdrop${isNavigationOpen ? ' is-visible' : ''}`}
        type='button'
        aria-label='Закрыть навигацию'
        onClick={() => setIsNavigationOpen(false)}
      />

      <aside
        className={`ui-sidebar${isNavigationOpen ? ' is-open' : ''}`}
        aria-label='Основная навигация'
      >
        <div className='ui-sidebar-brand'>
          <NavLink className='ui-brand' to='/dashboard' aria-label='POLab — на главную'>
            <span className='ui-brand-mark' aria-hidden='true'>
              PO
            </span>
            <span className='ui-brand-copy'>
              <strong>POLab</strong>
              <small>Лаборатория портфеля</small>
            </span>
          </NavLink>
          <button
            className='ui-icon-button ui-sidebar-close'
            type='button'
            aria-label='Закрыть меню'
            onClick={() => setIsNavigationOpen(false)}
          >
            <X size={16} weight='bold' />
          </button>
        </div>

        <nav className='ui-sidebar-nav' id='app-sidebar-navigation'>
          {navigationGroups.map((group) => (
            <section className='ui-nav-group' key={group.label}>
              <h2>{group.label}</h2>
              <div className='ui-nav-list'>
                {group.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <NavLink
                      className={({isActive}) =>
                        `ui-nav-link${isActive ? ' is-active' : ''}`
                      }
                      end={'end' in item ? item.end : false}
                      key={item.to}
                      to={item.to}
                    >
                      {({isActive}) => (
                        <>
                          <Icon size={17} weight={isActive ? 'fill' : 'regular'} />
                          <span>{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  )
                })}
              </div>
            </section>
          ))}
        </nav>

        <div className='ui-sidebar-footer'>
          <span className='ui-user-avatar' aria-hidden='true'>
            {initials || 'П'}
          </span>
          <span className='ui-user-copy'>
            <strong>{displayName}</strong>
            <small>{currentUser?.email || 'Рабочий профиль'}</small>
          </span>
        </div>
      </aside>

      <div className='ui-shell-main'>
        <header className='ui-shell-toolbar'>
          <div className='ui-shell-toolbar-start'>
            <button
              className='ui-icon-button ui-navigation-toggle'
              type='button'
              aria-label='Открыть навигацию'
              aria-controls='app-sidebar-navigation'
              aria-expanded={isNavigationOpen}
              onClick={() => setIsNavigationOpen(true)}
            >
              <List size={18} weight='bold' />
            </button>
            <span className='ui-shell-section'>Рабочее пространство</span>
          </div>

          <div className='ui-shell-toolbar-actions'>
            <button
              className='ui-icon-button'
              type='button'
              aria-label={
                resolvedTheme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'
              }
              title={resolvedTheme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
              onClick={toggleTheme}
            >
              {resolvedTheme === 'dark' ? (
                <Sun size={17} weight='regular' />
              ) : (
                <Moon size={17} weight='regular' />
              )}
            </button>
            <span className='ui-toolbar-user'>{displayName}</span>
            <button
              className='ui-icon-button'
              type='button'
              aria-label='Выйти из аккаунта'
              title='Выйти'
              onClick={handleLogout}
            >
              <SignOut size={17} weight='regular' />
            </button>
          </div>
        </header>

        <main className='ui-shell-content'>
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export {AppShell}
